import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { getDashboardMetrics } from '@/lib/operator-api';
import type { DashboardMetrics } from '@/lib/operator-api';
import { getTodayBookings } from '@/lib/operator-api/calendar-client';
import type { CalendarBooking } from '@/lib/operator-api/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  MessageSquare,
  Calendar,
  TrendingUp,
  Clock,
  Users,
  ArrowRight,
  Search,
  ClipboardList,
} from 'lucide-react';

import {
  StatCard,
  TodaysSchedule,
  PendingActions,
  ActivityFeed,
  PerformanceChart,
  RevenueSummary,
  MobileBottomNav,
} from './-dashboard-components';
import type { PendingAction, ActivityItem } from './-dashboard-components';

export const Route = createFileRoute('/_auth/operator/')({
  component: OperatorDashboard,
});

function OperatorDashboard() {
  const { t } = useTranslation();
  const { operator, permissions, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [todaysBookings, setTodaysBookings] = useState<CalendarBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch dashboard data
  useEffect(() => {
    if (!operator) return;
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch metrics and today's bookings in parallel
        const [metricsData, bookingsData] = await Promise.all([
          getDashboardMetrics(selectedBusinessId),
          getTodayBookings(selectedBusinessId || undefined).catch(() => [] as CalendarBooking[]),
        ]);

        if (controller.signal.aborted) return;
        setMetrics(metricsData);
        setTodaysBookings(bookingsData);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchData();

    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [operator, selectedBusinessId]);

  // Generate mock pending actions based on available data
  const pendingActions = useMemo<PendingAction[]>(() => {
    const actions: PendingAction[] = [];

    // Check for pending bookings that need confirmation
    const pendingBookings = todaysBookings.filter((b) => b.status === 'PENDING');
    if (pendingBookings.length > 0) {
      actions.push({
        id: 'pending-confirmations',
        type: 'waitlist',
        title: `${pendingBookings.length} pending confirmation${pendingBookings.length > 1 ? 's' : ''}`,
        subtitle: 'Review and confirm these bookings',
        timestamp: new Date(),
        priority: 'high',
        href: '/operator/bookings?status=pending',
      });
    }

    // Simulated actions for demo (in production, fetch from API)
    if (metrics && metrics.activeConversations > 0) {
      actions.push({
        id: 'unanswered-messages',
        type: 'message_unanswered',
        title: `${Math.min(metrics.activeConversations, 3)} unanswered messages`,
        subtitle: 'Customers waiting for response',
        timestamp: new Date(Date.now() - 15 * 60000),
        priority: metrics.activeConversations > 5 ? 'high' : 'medium',
        href: '/operator/conversations?filter=unanswered',
      });
    }

    return actions;
  }, [todaysBookings, metrics]);

  // Generate mock activity feed (in production, fetch from audit logs)
  const recentActivity = useMemo<ActivityItem[]>(() => {
    const activities: ActivityItem[] = [];

    // Generate from today's bookings
    todaysBookings.slice(0, 3).forEach((booking, index) => {
      activities.push({
        id: `booking-${booking.id}`,
        type: booking.status === 'COMPLETED' ? 'booking_completed' : 'booking_created',
        title: booking.status === 'COMPLETED'
          ? `${booking.serviceName} completed`
          : `New booking: ${booking.serviceName}`,
        description: `Customer: ${booking.customerName}`,
        timestamp: new Date(Date.now() - (index + 1) * 30 * 60000),
      });
    });

    // Add some simulated activities
    if (metrics && metrics.totalConversations > 0) {
      activities.push({
        id: 'conv-1',
        type: 'conversation_started',
        title: 'New conversation started',
        description: 'Customer inquiry via WhatsApp',
        timestamp: new Date(Date.now() - 45 * 60000),
      });
    }

    return activities.slice(0, 6);
  }, [todaysBookings, metrics]);

  // Calculate trends (mock - in production, compare with previous period)
  const trends = useMemo(() => {
    if (!metrics) return null;
    return {
      conversations: { value: 12, direction: 'up' as const },
      active: { value: 5, direction: 'down' as const },
      bookings: { value: 8, direction: 'up' as const },
      conversion: { value: 3, direction: 'up' as const },
    };
  }, [metrics]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#10B981] border-t-transparent" />
      </div>
    );
  }

  if (!operator) {
    return null;
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight font-display">
            {t('dashboard.welcome', { name: operator.name.split(' ')[0] })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {selectedBusiness ? (
              <>
                Viewing <strong className="text-foreground">{selectedBusiness.name}</strong>
              </>
            ) : (
              'Viewing all businesses'
            )}
          </p>
        </div>

        {/* Quick Stats Summary */}
        {!loading && metrics && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs py-1.5 px-3 border-[#10B981]/30 bg-[#10B981]/5">
              <Clock className="h-3 w-3 mr-1.5 text-[#10B981]" />
              Avg Response: {metrics.avgResponseTime?.toFixed(0) || '--'}min
            </Badge>
            <Badge variant="outline" className="text-xs py-1.5 px-3 border-[#0D4F3C]/20">
              <Users className="h-3 w-3 mr-1.5" />
              {metrics.pendingBookings || 0} Pending
            </Badge>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="flex items-center justify-between p-4">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Revenue Summary + Stat Cards Row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {/* Revenue Summary - Spans 2 columns on large screens */}
        <div className="sm:col-span-2 lg:col-span-2">
          <RevenueSummary
            todayRevenue={loading ? 0 : (metrics?.completedBookings || 0) * 85}
            yesterdayRevenue={loading ? 0 : (metrics?.completedBookings || 0) * 75}
            dailyGoal={1500}
            isLoading={loading}
          />
        </div>

        {/* Stat Cards - 3 cards filling remaining space */}
        <StatCard
          title={t('dashboard.totalConversations')}
          value={loading ? '--' : metrics?.totalConversations || 0}
          subtitle={t('dashboard.allTime')}
          icon={MessageSquare}
          trend={trends?.conversations}
          variant="primary"
        />
        <StatCard
          title={t('dashboard.completedBookings')}
          value={loading ? '--' : metrics?.completedBookings || 0}
          subtitle={t('dashboard.thisMonth')}
          icon={Calendar}
          trend={trends?.bookings}
        />
        <StatCard
          title={t('dashboard.conversionRate')}
          value={loading ? '--' : `${(metrics?.conversionRate || 0).toFixed(1)}%`}
          subtitle={t('dashboard.conversationsToBookings')}
          icon={TrendingUp}
          trend={trends?.conversion}
        />
      </div>

      {/* Performance Chart - Full Width */}
      <PerformanceChart />

      {/* Main Content Grid - Asymmetric 2 columns */}
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        {/* Left Column - Larger */}
        <div className="space-y-6">
          {/* Today's Schedule */}
          <TodaysSchedule bookings={todaysBookings} isLoading={loading} />
        </div>

        {/* Right Column - Narrower */}
        <div className="space-y-6">
          {/* Pending Actions */}
          <PendingActions actions={pendingActions} isLoading={loading} />

          {/* Activity Feed */}
          <ActivityFeed activities={recentActivity} isLoading={loading} />
        </div>
      </div>

      {/* Quick Actions Bar - Hidden on mobile (use bottom nav instead) */}
      <Card className="bg-[#fdfcfb] border-[#f3efe9] hidden sm:block">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground mr-2">Quick Actions:</span>
            {permissions?.canViewConversations && (
              <Button asChild size="sm" className="bg-[#0D4F3C] hover:bg-[#0D4F3C]/90">
                <Link to="/operator/conversations" className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  {t('dashboard.viewConversations')}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </Button>
            )}
            {permissions?.canSearchCustomers && (
              <Button asChild variant="outline" size="sm" className="border-[#0D4F3C]/20 hover:bg-[#0D4F3C]/5">
                <Link to="/operator/customers" className="flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  {t('dashboard.searchCustomers')}
                </Link>
              </Button>
            )}
            <Button asChild variant="outline" size="sm" className="border-[#0D4F3C]/20 hover:bg-[#0D4F3C]/5">
              <Link to="/operator/calendar" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                View Calendar
              </Link>
            </Button>
            {permissions?.canViewAuditLogs && (
              <Button asChild variant="ghost" size="sm">
                <Link to="/operator/audit-logs" className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4" />
                  {t('dashboard.viewAuditLogs')}
                </Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}
